import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import api, { formatApiError } from '@/lib/api';
import { toast } from '@/components/ui/sonner';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { formatAptRent } from '@/lib/apartmentFormat';

function pairKey(aptId, appId) {
  return `${aptId}::${appId}`;
}

export default function BulkMatchListingsDialog({
  open,
  onOpenChange,
  selectedApartments,
  onComplete,
}) {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [sections, setSections] = useState([]);
  const [selectedPairs, setSelectedPairs] = useState({});

  const loadEligible = useCallback(async () => {
    if (!selectedApartments?.length) return;
    setLoading(true);
    try {
      const results = await Promise.all(
        selectedApartments.map((apt) =>
          api.get(`/apartments/${apt.id}/eligible-applicants`).then((r) => ({
            apartment: apt,
            eligible: r.data?.eligible || [],
            applicants: r.data?.applicants || [],
          }))
        )
      );
      setSections(results);
      const initial = {};
      results.forEach(({ apartment, eligible }) => {
        eligible.forEach((row) => {
          initial[pairKey(apartment.id, row.application_id)] = true;
        });
      });
      setSelectedPairs(initial);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || t('admin.bulkMatch.loadError'));
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  }, [selectedApartments, onOpenChange, t]);

  useEffect(() => {
    if (open) {
      setMessage('');
      loadEligible();
    }
  }, [open, loadEligible]);

  const selectedItems = useMemo(() => {
    const items = [];
    sections.forEach(({ apartment, eligible }) => {
      (eligible || []).forEach((row) => {
        if (selectedPairs[pairKey(apartment.id, row.application_id)]) {
          items.push({
            apartment_id: apartment.id,
            application_id: row.application_id,
          });
        }
      });
    });
    return items;
  }, [sections, selectedPairs]);

  const togglePair = (aptId, appId, checked) => {
    setSelectedPairs((prev) => ({
      ...prev,
      [pairKey(aptId, appId)]: checked,
    }));
  };

  const toggleAllForApt = (aptId, rows, checked) => {
    setSelectedPairs((prev) => {
      const next = { ...prev };
      rows.forEach((row) => {
        if (row.eligible) {
          next[pairKey(aptId, row.application_id)] = checked;
        }
      });
      return next;
    });
  };

  const handleSubmit = async () => {
    if (selectedItems.length === 0) {
      toast.error(t('admin.bulkMatch.noSelection'));
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post('/admin/bulk-listing-proposals', {
        items: selectedItems.map(({ apartment_id, application_id }) => ({
          apartment_id,
          application_id,
        })),
        message: message.trim() || null,
      });
      const created = res.data?.created?.length || 0;
      const skipped = res.data?.skipped?.length || 0;
      const drafts = res.data?.pending_email_drafts || [];
      if (created) {
        toast.success(`${created} ${t('admin.bulkMatch.proposalsCreated')}`);
      }
      if (skipped) {
        toast.info(`${skipped} ${t('admin.bulkMatch.proposalsSkipped')}`);
      }
      onComplete({ drafts });
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || t('admin.bulkMatch.failed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="bulk-match-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg">{t('admin.bulkMatch.title')}</DialogTitle>
          <p className="text-xs text-[#5a5a5a]">{t('admin.bulkMatch.subtitle')}</p>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-[#0B3D2E] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="space-y-5">
            <div>
              <Label className="text-xs font-medium">{t('admin.bulkMatch.messageLabel')}</Label>
              <Textarea
                rows={3}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={t('admin.bulkMatch.messagePlaceholder')}
                className="mt-1 text-sm"
                data-testid="bulk-match-message"
              />
            </div>

            {sections.map(({ apartment, applicants }) => {
              const eligibleRows = applicants.filter((r) => r.eligible);
              const allSelected = eligibleRows.length > 0 && eligibleRows.every(
                (r) => selectedPairs[pairKey(apartment.id, r.application_id)]
              );
              return (
                <div
                  key={apartment.id}
                  className="border border-[#2B2B2B]/10 rounded-lg overflow-hidden"
                  data-testid={`bulk-match-apt-${apartment.id}`}
                >
                  <div className="bg-[#F5F3EF] px-3 py-2 flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-medium text-sm text-[#2B2B2B] truncate">{apartment.title}</p>
                      <p className="text-[10px] text-[#5a5a5a]">
                        {formatAptRent(apartment)} &middot; {eligibleRows.length} {t('admin.bulkMatch.eligibleCount')}
                      </p>
                    </div>
                    {eligibleRows.length > 0 && (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="text-[10px] h-7 shrink-0"
                        onClick={() => toggleAllForApt(apartment.id, eligibleRows, !allSelected)}
                      >
                        {allSelected ? t('admin.bulkMatch.deselectAll') : t('admin.bulkMatch.selectAll')}
                      </Button>
                    )}
                  </div>
                  {eligibleRows.length === 0 ? (
                    <p className="text-xs text-[#5a5a5a] px-3 py-4">{t('admin.bulkMatch.noEligible')}</p>
                  ) : (
                    <ul className="divide-y divide-[#2B2B2B]/5">
                      {eligibleRows.map((row) => (
                        <li
                          key={row.application_id}
                          className="flex items-center gap-3 px-3 py-2 text-sm"
                        >
                          <Checkbox
                            checked={!!selectedPairs[pairKey(apartment.id, row.application_id)]}
                            onCheckedChange={(v) => togglePair(apartment.id, row.application_id, !!v)}
                            data-testid={`bulk-pair-${apartment.id}-${row.application_id}`}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-[#2B2B2B] truncate">{row.name || row.email}</p>
                            <p className="text-[10px] text-[#5a5a5a]">
                              {t('admin.bulkMatch.score')}: {Math.round(row.score_total || 0)}
                              {row.income_ratio > 0 && (
                                <> &middot; {t('admin.bulkMatch.incomeRatio')}: {row.income_ratio}x</>
                              )}
                            </p>
                          </div>
                          <Badge className="bg-slate-100 text-slate-600 border-0 text-[10px] capitalize">
                            {row.status}
                          </Badge>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            className="bg-[#0B3D2E] text-white"
            disabled={submitting || loading || selectedItems.length === 0}
            onClick={handleSubmit}
            data-testid="bulk-match-submit"
          >
            {submitting
              ? t('common.loading')
              : `${t('admin.bulkMatch.submit')} (${selectedItems.length})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
